import React, { useCallback, useEffect, useMemo, useState } from "react";

import GoogleMapReact from "google-map-react";

import {
  ModalProvider,
  checkExperienceCookieConsent,
  getGoogleKey,
  isFeatureActiveOnCurrentFarm,
  toggleCookieBannerVisibility,
  useAppType,
  useCookieConsent,
  useGetFarmReportingYear,
  useGetFarms,
  useGetFieldShapes,
  useGetFieldsByFarmId,
  useSelectedField,
  useTranslation,
} from "utils";

import { FEATURE_FLAG_SLUGS } from "consts";

import { Drawer, EmptyStateCard, Flex, Icon, InputSearch, MainPage, MapCookieConsentReminder, MetaData } from "components";

import { FarmSwitcher } from "pages/app/all-field-view/components/FarmSwitcher";
import { FieldFilterOptionsChips } from "pages/app/all-field-view/components/FieldFilterOptionsChips";
import { FieldToolbarAddMenu } from "pages/app/all-field-view/components/FieldToolbarAddMenu";
import { NoMatchesFeedback } from "pages/app/all-field-view/components/NoMatchesFeedback";
import { useFieldStatusMap } from "pages/app/all-field-view/hooks/useFieldStatusMap";
import { useMapPolygons } from "pages/app/all-field-view/hooks/useMapPolygons";
import { useMapViewFilters } from "pages/app/all-field-view/hooks/useMapViewFilters";
import { useSubmissionStatus } from "pages/app/all-field-view/hooks/useSubmissionStatus";
import { getFarmOpenTasksCount } from "pages/app/all-field-view/utils/AllFieldsUtils";
import { FieldDetails } from "pages/app/fields/components/FieldDetails";
import { FieldEnrollmentDrawer } from "pages/app/fields/components/FieldEnrollmentDrawer";
import { FieldUpload } from "pages/app/fields/components/FieldUpload";

type FieldMarkerProps = {
  lat: number;
  lng: number;
  isComplete: boolean;
  onClick: () => void;
};

const FieldMarker = ({ isComplete, onClick }: FieldMarkerProps) => (
  <div className={`field-map-marker${isComplete ? " complete" : " incomplete"}`} onClick={onClick} role="button" tabIndex={0}>
    <span className="field-map-marker-dot" />
  </div>
);

const MAP_OPTIONS = (maps: typeof google.maps) => ({
  streetViewControl: false,
  keyboardShortcuts: false,
  scaleControl: true,
  fullscreenControl: false,
  rotateControl: false,
  mapTypeControl: false,
  mapTypeId: maps.MapTypeId.HYBRID,
  zoomControl: true,
  clickableIcons: false,
  gestureHandling: "cooperative",
  disableDoubleClickZoom: true,
  styles: [{ featureType: "poi.business", elementType: "labels", stylers: [{ visibility: "off" }] }],
});

// matches the field details Drawer's own default desktop width (Drawer.tsx's getWidth())
const FIELD_DETAILS_DRAWER_WIDTH_PX = 644;

export const FieldsMapView = () => {
  const { t } = useTranslation("allFieldView");
  const appType = useAppType();
  const { currentFarm, isLoading: isFarmsLoading } = useGetFarms();
  const {
    data: { activeFarmReportingYear },
  } = useGetFarmReportingYear(currentFarm?.id ?? null);
  const { data: fields, isLoading: isFieldsLoading } = useGetFieldsByFarmId({ farmId: currentFarm?.id ?? "" });
  const { fieldShapes, fieldShapesLoading } = useGetFieldShapes({ enabled: !!currentFarm?.id, fieldCount: fields?.length });
  const fieldStatusMap = useFieldStatusMap(currentFarm, activeFarmReportingYear ?? new Date().getFullYear());

  const [googleMap, setGoogleMap] = useState<google.maps.Map | null>(null);
  const [googleMaps, setGoogleMaps] = useState<typeof google.maps | null>(null);
  const [showFieldUploadDrawer, setShowFieldUploadDrawer] = useState(false);
  const [showBulkEnrollDrawer, setShowBulkEnrollDrawer] = useState(false);
  const { selectedFieldId, setSelectedFieldId } = useSelectedField(fields);

  const carbonProgram = currentFarm?.carbonProgram ?? null;
  const isSoilPlus3DataGapFeatureActive = isFeatureActiveOnCurrentFarm(FEATURE_FLAG_SLUGS.SOIL_PLUS_3_DATA_GAP);
  const { isDataSubmitted } = useSubmissionStatus(activeFarmReportingYear);

  const hasNoFields = !isFarmsLoading && !isFieldsLoading && (!fields || fields.length === 0);
  const hasMapCookieConsent = useCookieConsent(checkExperienceCookieConsent);
  const defaultCenter = useMemo(() => fields?.[0]?.location ?? { lat: 52.5224832, lng: 13.3906564 }, [fields]);

  const {
    filteredFieldIds,
    setFilteredFieldIds,
    appliedCropIds,
    setAppliedCropIds,
    appliedYears,
    setAppliedYears,
    appliedDataStatus,
    setAppliedDataStatus,
    searchQuery,
    setSearchQuery,
    setSelectedFilterOptionsCount,
    itemsWithMissingDataByFieldId,
    filteredFields,
    filteredFieldShapes,
    hasNoFilteredFields,
    handleClearFilters,
  } = useMapViewFilters({ fields, fieldShapes, fieldStatusMap, farmId: currentFarm?.id, hasNoFields });

  const { fieldsWithOpenTasksCount } = getFarmOpenTasksCount(itemsWithMissingDataByFieldId, []);

  // Suppress the native Iubenda banner in favor of the MapCookieConsentReminder card while this view is mounted
  useEffect(() => {
    toggleCookieBannerVisibility(false);
    return () => toggleCookieBannerVisibility(true);
  }, []);

  // Disable map interaction when there are no fields
  useEffect(() => {
    if (!googleMap) return;
    googleMap.setOptions({
      gestureHandling: hasNoFields ? "none" : "cooperative",
      draggable: !hasNoFields,
      scrollwheel: !hasNoFields,
      keyboardShortcuts: !hasNoFields,
    });
  }, [googleMap, hasNoFields]);

  // Reset map to default center when all fields are deleted — deliberately not keyed on defaultCenter, which
  // gets a new reference on any unrelated fields refetch and would otherwise yank the map off a selected field
  useEffect(() => {
    if (!googleMap || !hasNoFields) return;
    googleMap.setCenter(defaultCenter);
    googleMap.setZoom(13);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleMap, hasNoFields]);

  // Keep the selected field visible next to the field details drawer, shifted right by half its width on desktop.
  // Not keyed on fieldShapes, so saving data in the drawer (which refetches it) doesn't re-pan away from wherever
  // the user has since scrolled/zoomed
  useEffect(() => {
    if (!googleMap || !selectedFieldId) return;
    const center = fieldShapes.find((shape) => shape.fieldId === selectedFieldId)?.center;
    if (!center) return;

    googleMap.panTo(center);
    if (appType.desktop) googleMap.panBy(FIELD_DETAILS_DRAWER_WIDTH_PX / 2, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleMap, selectedFieldId, appType.desktop]);

  useMapPolygons(googleMap, googleMaps, filteredFieldShapes, fieldStatusMap, setSelectedFieldId);

  const handleGoogleApiLoaded = useCallback(({ map, maps }: { map: google.maps.Map; maps: typeof google.maps }) => {
    setGoogleMap(map);
    setGoogleMaps(maps);
  }, []);

  const markers = useMemo(() => {
    const centerById = Object.fromEntries(fieldShapes.map((s) => [s.fieldId, s.center]));
    return filteredFields
      .filter((field) => centerById[field.id] && fieldStatusMap[field.id] !== undefined)
      .map((field) => ({
        id: field.id,
        lat: centerById[field.id].lat,
        lng: centerById[field.id].lng,
        isComplete: fieldStatusMap[field.id].isComplete,
      }));
  }, [filteredFields, fieldStatusMap, fieldShapes]);

  const navigationCrumbs = {
    crumbs: [{ name: "", content: <FarmSwitcher />, replacedByFarmSwitch: true }, { name: t("fieldTimeline:header.fieldManagementCrumb") }],
  };

  return (
    <MainPage fullWidth loading={isFarmsLoading || isFieldsLoading} pageName="all-field-page" navigationCrumbs={navigationCrumbs} showMobileAlert>
      <MetaData title={t("allFieldView")} />
      <div className="field-map-toolbar">
        <Flex align="center" className="field-map-search-container">
          <InputSearch
            dataTestId="filter-map-fields"
            className="field-map-search-input"
            isSmallVariant
            label={t("search")}
            onChange={(e) => setSearchQuery(e.target.value)}
            value={searchQuery}
            disabled={hasNoFields}
          />
        </Flex>
        <FieldToolbarAddMenu
          fields={fields}
          carbonProgram={carbonProgram}
          isSoilPlus3DataGapFeatureActive={isSoilPlus3DataGapFeatureActive}
          isDataSubmitted={isDataSubmitted}
          fieldsWithOpenTasksCount={fieldsWithOpenTasksCount}
          onAddFields={() => setShowFieldUploadDrawer(true)}
          onEnrollFields={() => setShowBulkEnrollDrawer(true)}
        />
        <FieldFilterOptionsChips
          fields={fields ?? []}
          filteredFieldIds={filteredFieldIds}
          setFilteredFieldIds={setFilteredFieldIds}
          setSelectedFilterOptionsCount={setSelectedFilterOptionsCount}
          appliedCropIds={appliedCropIds}
          appliedYears={appliedYears}
          appliedDataStatus={appliedDataStatus}
          setAppliedCropIds={setAppliedCropIds}
          setAppliedYears={setAppliedYears}
          setAppliedDataStatus={setAppliedDataStatus}
          farmId={currentFarm?.id || ""}
          handleClearFilters={handleClearFilters}
          itemsWithMissingDataByFieldId={itemsWithMissingDataByFieldId}
        />
      </div>
      <div className="field-map-container">
        <div className="field-map-google-wrapper">
          {!fieldShapesLoading && hasMapCookieConsent && (
            <GoogleMapReact
              bootstrapURLKeys={{ key: getGoogleKey(), libraries: ["drawing"] }}
              defaultCenter={defaultCenter}
              defaultZoom={13}
              options={MAP_OPTIONS}
              yesIWantToUseGoogleMapApiInternals
              onGoogleApiLoaded={handleGoogleApiLoaded}
            >
              {markers.map((marker) => (
                <FieldMarker key={marker.id} lat={marker.lat} lng={marker.lng} isComplete={marker.isComplete} onClick={() => setSelectedFieldId(marker.id)} />
              ))}
            </GoogleMapReact>
          )}
        </div>
        {!fieldShapesLoading && !hasMapCookieConsent && (
          <div className="field-map-empty-overlay">
            <div className="field-map-empty-state">
              <MapCookieConsentReminder />
            </div>
          </div>
        )}
        {hasNoFields && (
          <div className="field-map-empty-overlay blocked">
            <div className="field-map-empty-state">
              <EmptyStateCard
                iconName="klim/field"
                title={t("noFields.title")}
                description={t("noFields.description")}
                buttonText={t("fieldContextMenu.addFields")}
                onButtonClick={() => setShowFieldUploadDrawer(true)}
              />
            </div>
          </div>
        )}
        {hasNoFilteredFields && (
          <div className="field-map-empty-overlay">
            <div className="field-map-empty-card">
              <NoMatchesFeedback handleClearFilters={handleClearFilters} />
            </div>
          </div>
        )}
        {!hasNoFields && (
          <div className="field-map-legend">
            <div className="field-map-legend-item">
              <span className="field-map-legend-indicator fill enrolled" />
              {t("mapView.legendEnrolled")}
            </div>
            <div className="field-map-legend-item">
              <span className="field-map-legend-indicator dot complete" />
              {t("mapView.legendDataComplete")}
            </div>
            <div className="field-map-legend-item">
              <span className="field-map-legend-indicator dot incomplete" />
              {t("mapView.legendDataIncomplete")}
            </div>
            <div className="field-map-legend-item">
              <Icon iconName="klim/not-enrolled" size="xs" className="field-map-legend-indicator fill" strokeWidth={1} />
              {t("mapView.legendNotEnrolled")}
            </div>
          </div>
        )}
      </div>
      <Drawer
        className="field-details-drawer"
        title={t("farm:fieldsTab.fieldDetails")}
        visible={!!selectedFieldId}
        onClose={() => setSelectedFieldId(null)}
        styles={{ body: { padding: "0 2rem" } }}
      >
        <ModalProvider>
          <FieldDetails fieldId={selectedFieldId ?? undefined} setShowFieldEditDrawer={() => setSelectedFieldId(null)} />
        </ModalProvider>
      </Drawer>
      <Drawer title={t("titleUpdateDrawer")} visible={showFieldUploadDrawer} onClose={() => setShowFieldUploadDrawer(false)}>
        <FieldUpload onFinish={() => setShowFieldUploadDrawer(false)} />
      </Drawer>
      {showBulkEnrollDrawer && <FieldEnrollmentDrawer visible={showBulkEnrollDrawer} onClose={() => setShowBulkEnrollDrawer(false)} fields={fields ?? []} />}
    </MainPage>
  );
};
